/**
 * Pathology pyramid model + tile-loader.
 *
 * One SOP-Instance-per-resolution-level is the IDC / CPTAC-BRCA
 * convention. This module turns a study's series worth of instances
 * into a pyramid index sorted by resolution, then serves tiles out
 * of `GET /viewer-api/wado/studies/{s}/series/{s}/instances/{i}/frames/{n}`
 * on demand, with a bounded LRU cache + visible-set prefetch.
 *
 *   level[0] = biggest  (≈ base  40× scan — thousands of tiles)
 *   level[N] = smallest (≈ overview or thumbnail)
 *
 * The client never asks for the whole DICOM file of a high-res level —
 * only the frames it needs. Each frame is one uncompressed 256×256
 * RGB tile ≈ 196 KB on the wire.
 */

import type { StudyInstance } from "@viewer/api/client";
import { auth, setAuthState } from "@viewer/auth/store";
import { decodeJ2K, isJ2KMediaType } from "./j2kDecoder";

/// Called on every 401 from the WADO endpoints. The SPA's silent
/// refresh timer handles the normal "near-expiry" case; this hook is
/// the fallback for when a JWT has already expired by the time a
/// request lands (user left the tab open overnight, say). Clearing
/// the auth state + redirecting to /login kicks off a fresh OIDC
/// flow and drops the user back on the same URL they were on.
function onAuthFailure(): never {
  // eslint-disable-next-line no-console
  console.warn("[wsi] 401 from WADO — token likely expired, redirecting to /login");
  try { setAuthState(null); } catch { /* noop */ }
  const back = location.pathname + location.search + location.hash;
  location.replace("/login?return_to=" + encodeURIComponent(back));
  throw new Error("auth expired");
}

const BASE = "/viewer-api";

/** Mint the Authorization + Accept header pair every WADO call needs.
 *  The viewer-api proxy (Bun server.ts) rejects non-Bearer requests
 *  with 401, so forgetting this header just leaves the pyramid card
 *  stuck on "Indexing pyramid…" forever. */
function jsonHeaders(): HeadersInit {
  const s = auth();
  const h: Record<string, string> = { Accept: "application/json" };
  if (s) h["Authorization"] = `Bearer ${s.accessToken}`;
  return h;
}
function octetHeaders(): HeadersInit {
  const s = auth();
  const h: Record<string, string> = { Accept: "application/octet-stream" };
  if (s) h["Authorization"] = `Bearer ${s.accessToken}`;
  return h;
}

/** Shape of the JSON our Zig vpd-dicomweb service returns from
 *  `.../instances/{uid}/metadata` — see handleWadoMetadata. */
export interface InstanceMeta {
  rows:           number;
  cols:           number;
  samples:        number;
  bitsAllocated:  number;
  nFrames:        number;
  totalCols:      number;
  totalRows:      number;
  pixelBytes:     number;
  /** Micrometres per pixel on the X axis at this level. 0 when the
   *  source DICOM didn't carry ImagedVolumeWidth or PixelSpacing
   *  (falls back to a default in the viewport). */
  mppX?:          number;
}

export interface PyramidLevel {
  instanceUid:  string;
  meta:         InstanceMeta;
  /** ceil(totalCols / cols) — number of tile columns in this level. */
  tilesPerRow:  number;
  tilesPerCol:  number;
  /** Scale relative to level 0 (the largest). level0 = 1.0. */
  scale:        number;
}

export interface Pyramid {
  studyUid:     string;
  seriesUid:    string;
  /** μm/pixel on the base (level 0) — the authoritative physical scale
   *  the viewport uses to derive objective-equivalent magnification.
   *  Undefined when the source DICOM didn't carry scale tags. */
  mppX0?:       number;
  /** Largest → smallest. `levels[0]` owns the slide coordinate system. */
  levels:       PyramidLevel[];
  /** Slide width in pixels at level 0. */
  slideWidth:   number;
  slideHeight:  number;
}

async function fetchMeta(inst: StudyInstance): Promise<InstanceMeta> {
  const url = `${BASE}/wado/studies/${inst.studyUid}/series/${inst.seriesUid}/instances/${inst.instanceUid}/metadata`;
  const rsp = await fetch(url, { headers: jsonHeaders() });
  if (rsp.status === 401) onAuthFailure();
  if (!rsp.ok) throw new Error(`metadata ${inst.instanceUid}: ${rsp.status}`);
  return (await rsp.json()) as InstanceMeta;
}

/** Build a pyramid from a series worth of SM / GM DICOM instances.
 *  Instances with zero TotalPixelMatrix dimensions (label / overview
 *  non-tiled single-frame SOPs) are kept as the smallest level if
 *  nothing better is available — they still render in the overview.
 *
 *  Returns `null` when called with an empty instance list (e.g. the
 *  parent's slides prop hasn't populated yet). The caller treats
 *  `null` as "still loading" rather than surfacing it as an error —
 *  createResource-backed callers previously saw this spelled as
 *  `Uncaught Error: no instances` spam on every re-render. */
export async function buildPyramid(instances: StudyInstance[]): Promise<Pyramid | null> {
  if (!instances.length) return null;
  const seriesUid = instances[0].seriesUid;
  const studyUid  = instances[0].studyUid;

  const metas = await Promise.all(instances.map(async (inst) => {
    try {
      const m = await fetchMeta(inst);
      return { inst, m };
    } catch {
      return null;
    }
  }));

  const levels: PyramidLevel[] = [];
  for (const hit of metas) {
    if (!hit) continue;
    const { inst, m } = hit;
    const totalCols = m.totalCols > 0 ? m.totalCols : m.cols;
    const totalRows = m.totalRows > 0 ? m.totalRows : m.rows;
    const tilesPerRow = Math.max(1, Math.ceil(totalCols / m.cols));
    const tilesPerCol = Math.max(1, Math.ceil(totalRows / m.rows));
    levels.push({
      instanceUid:  inst.instanceUid,
      meta:         { ...m, totalCols, totalRows },
      tilesPerRow,
      tilesPerCol,
      scale:        1,   // filled in below
    });
  }
  if (!levels.length) return null;

  // Sort biggest → smallest.
  levels.sort((a, b) => b.meta.totalCols * b.meta.totalRows
                      - a.meta.totalCols * a.meta.totalRows);
  // Discard non-tissue instances. IDC bundles a "label" image (barcode
  // sticker, typically near-square) and an "overview" image (photo
  // of the whole slide, typically a long narrow rectangle) alongside
  // the actual pyramid. Two heuristics in order of specificity:
  //
  //   (1) aspect-ratio outlier: anything whose log-aspect-ratio is
  //       further than 0.5 (≈ ±65%) from the base. Catches the
  //       overview strip (aspect ≈ 3:1 on a slide with 1:1 base).
  //
  //   (2) area-outlier smallest level: after (1), if the smallest
  //       remaining level has less than ⅓ the pixel area of the
  //       next-smallest, it's almost certainly a thumbnail-sized
  //       LABEL image that sneaked through the aspect filter because
  //       barcode stickers happen to be roughly square. A legitimate
  //       pyramid level is usually ≈ ¼ the area of the next one up,
  //       so ⅓ is a safe lower bound that won't accidentally reject
  //       a real thumbnail.
  const base0 = levels[0];
  const baseAR = Math.log(base0.meta.totalCols / Math.max(1, base0.meta.totalRows));
  let tissueLevels = levels.filter((l) => {
    const ar = Math.log(l.meta.totalCols / Math.max(1, l.meta.totalRows));
    return Math.abs(ar - baseAR) < 0.5;
  });
  if (tissueLevels.length === 0) return null;
  // `tissueLevels` is already sorted biggest→smallest because `levels`
  // was. Identify LABEL-sized tail entries and drop them. Previous
  // heuristic ("last area < ⅓ of prev") broke on native DICOM
  // pyramids that step by 4× (not 2×) per level — a healthy 1.25×
  // level would look like "too small relative to prev" and get
  // axed, cascading up the ladder and leaving us with just the base.
  //
  // New heuristic: absolute-size. Labels / pure-thumbnail frames
  // shipped alongside IDC tiled instances have single-frame matrices
  // around 300–450 px on a side. Anything with totalCols >= 1500
  // (= at least 6×6 tiles at 256 px) is a real pyramid level, keep.
  // Below that the entry is almost certainly a label/overview
  // thumbnail the aspect-ratio filter didn't catch.
  const LABEL_MAX_COLS = 1500;
  while (tissueLevels.length >= 2) {
    const last = tissueLevels[tissueLevels.length - 1];
    if (last.meta.totalCols < LABEL_MAX_COLS) tissueLevels.pop();
    else break;
  }
  levels.length = 0;
  levels.push(...tissueLevels);
  // Normalise scale against level 0.
  const base = levels[0];
  for (const l of levels) l.scale = l.meta.totalCols / base.meta.totalCols;

  return {
    studyUid, seriesUid, levels,
    slideWidth:  base.meta.totalCols,
    slideHeight: base.meta.totalRows,
    mppX0:       (base.meta.mppX && base.meta.mppX > 0) ? base.meta.mppX : undefined,
  };
}

/** Pick the pyramid level for the current zoom.
 *
 *  Rule (matches Aperio WebViewer, Philips IMS, Slim):
 *    1. zoom ≥ 1  → always base (level 0). Any upsampled base is
 *       sharper than an upsampled lower level because DWT has
 *       already discarded the high-frequency detail at the lower
 *       levels. This is the load-bearing rule for "pathologist's
 *       100× / 300× view" — digital zoom past native MUST come
 *       from level 0.
 *    2. otherwise, the coarsest level whose scale ≥ zoom — unless
 *       we're within the top 25 % of that level's range, in which
 *       case bias to the next-finer level so the downsample looks
 *       crisp instead of smeared.
 *
 *  `zoom` is screen-pixels-per-slide-pixel (slide = level-0 pixel). */
export function pickLevel(pyr: Pyramid, zoom: number): PyramidLevel {
  if (zoom >= 1.0) return pyr.levels[0];
  for (let i = pyr.levels.length - 1; i >= 0; i--) {
    const s = pyr.levels[i].scale;
    if (s >= zoom) {
      // Bias: if within 25 % of the boundary, prefer next-finer.
      if (zoom / s > 0.75 && i > 0) return pyr.levels[i - 1];
      return pyr.levels[i];
    }
  }
  return pyr.levels[0];
}

/** Given a visible rectangle in level-0 coordinates, return the
 *  tile indices needed at the chosen level. */
export function visibleTiles(
  pyr:   Pyramid,
  level: PyramidLevel,
  x0:    number, y0: number, x1: number, y1: number,
): Array<{ tx: number; ty: number; frame: number }> {
  // Inclusive-inclusive tile range. Use floor((end - 1)/tile) for the
  // upper bound so a rectangle whose right edge sits exactly on a tile
  // boundary doesn't pull in the next tile column.
  const lvlX0 = Math.max(0, Math.floor(x0 * level.scale / level.meta.cols));
  const lvlY0 = Math.max(0, Math.floor(y0 * level.scale / level.meta.rows));
  const lvlX1 = Math.min(level.tilesPerRow - 1,
    Math.max(lvlX0, Math.floor((x1 * level.scale - 1) / level.meta.cols)));
  const lvlY1 = Math.min(level.tilesPerCol - 1,
    Math.max(lvlY0, Math.floor((y1 * level.scale - 1) / level.meta.rows)));
  const out: Array<{ tx: number; ty: number; frame: number }> = [];
  for (let ty = lvlY0; ty <= lvlY1; ty++) {
    for (let tx = lvlX0; tx <= lvlX1; tx++) {
      const frame = ty * level.tilesPerRow + tx + 1;   // 1-based
      if (frame > level.meta.nFrames) continue;
      out.push({ tx, ty, frame });
    }
  }
  return out;
}

// --------------------------------------------------------------------
//  Tile cache — per-session, bounded, with neighbour prefetch.
// --------------------------------------------------------------------

export interface Tile {
  level: PyramidLevel;
  tx:    number;
  ty:    number;
  image: ImageBitmap;  // ready for canvas.drawImage
}

interface CacheKey {
  instanceUid: string;
  frame: number;
}
function keyOf(k: CacheKey): string { return `${k.instanceUid}:${k.frame}`; }

// Two-tier cache.
//
// `tiles` — main LRU for whatever the user is actively looking at.
// 2048 entries ≈ 500 MB at 256×256 RGBA; plenty of headroom for a
// zoomed-in session but doesn't grow unbounded.
//
// `stickyTiles` — never-evict slot for the coarsest-level minimap
// tiles and any other "we'll look at this every frame forever" set.
// Callers opt a tile in by calling `setStickyTile(...)`. There are at
// most ~tilesPerRow × tilesPerCol of the coarsest level in here —
// bounded by the slide and orders of magnitude smaller than `tiles`.
//
// Splitting them fixes the thrash that the old single-cache design
// produced: with one 512-entry LRU across every level, a pan at high
// zoom evicted the minimap's coarsest tiles first (they were inserted
// earliest, so oldest in insertion order), and the next rAF re-ran
// drawMinimap, re-fetched them, and evicted main-view tiles in turn.
// Visible symptom: 2000+ requests/sec, the browser hang the
// pathologist-demo session surfaced.
const MAX_CACHE = 2048;
const tiles       = new Map<string, Tile>();
const stickyTiles = new Map<string, Tile>();
const inflight    = new Map<string, Promise<Tile>>();
/** Tiles whose decode failed at least once in this session, mapped to
 *  the cumulative attempt count. The draw loop treats any entry as
 *  "handled" (via `tileIsSettled` below) so it does not re-queue a
 *  fetch on every rAF — which was the core of the 2000-request-per-
 *  second storm on the native-JP2 Ki67 slide. Up to `MAX_TILE_RETRIES`
 *  background retries are scheduled with backoff before the entry
 *  becomes a permanent latch. Transient flakes (truncated JP2 stream
 *  on a partial body, momentary socat hiccup) recover without the
 *  user having to click. */
const failedTiles = new Map<string, number>();
const MAX_TILE_RETRIES = 3;
const RETRY_BACKOFF_MS = [400, 1500, 4000];

export function cachedTile(level: PyramidLevel, tx: number, ty: number): Tile | undefined {
  const frame = ty * level.tilesPerRow + tx + 1;
  const key = keyOf({ instanceUid: level.instanceUid, frame });
  return stickyTiles.get(key) ?? tiles.get(key);
}

/** Number of tiles currently in-flight. Viewer wires this into a
 *  "loading…" indicator so the user gets feedback while tier-2 fetches
 *  + WASM decodes are in progress. */
export function inflightCount(): number { return inflight.size; }

/** Clear any failure latch on a tile and request it immediately. Used
 *  by the viewer when the user clicks a still-blank square to force a
 *  priority re-fetch (tile whose retries were exhausted is given a
 *  fresh budget, out-of-turn). */
export function prioritizeTiles(
  pyr: Pyramid, level: PyramidLevel,
  coords: Array<{ tx: number; ty: number }>,
): Promise<Tile[]> {
  for (const { tx, ty } of coords) {
    const frame = ty * level.tilesPerRow + tx + 1;
    const key = keyOf({ instanceUid: level.instanceUid, frame });
    failedTiles.delete(key);
  }
  return getTilesBatch(pyr, level, coords);
}

/** Like `cachedTile` but also returns `true` (as a sentinel) when the
 *  tile has permanently failed. Draw loops that only want to answer
 *  "is this tile pending another network attempt?" should consult
 *  this instead of `cachedTile` alone. */
export function tileIsSettled(level: PyramidLevel, tx: number, ty: number): boolean {
  const frame = ty * level.tilesPerRow + tx + 1;
  const key = keyOf({ instanceUid: level.instanceUid, frame });
  return stickyTiles.has(key) || tiles.has(key) || failedTiles.has(key);
}

/** Internal helper — insert a tile into whichever store the caller
 *  nominated via `opts.sticky`. LRU-evict against `tiles` only; the
 *  sticky map is intentionally unbounded (its population is capped by
 *  the slide's coarsest-level tile count, which is tiny). */
function storeTile(key: string, tile: Tile, sticky: boolean): void {
  if (sticky) {
    stickyTiles.set(key, tile);
    return;
  }
  if (tiles.size >= MAX_CACHE) {
    const firstKey = tiles.keys().next().value;
    if (firstKey) tiles.delete(firstKey);
  }
  tiles.set(key, tile);
}

/** Extract the body bytes of the first part of a multipart/related
 *  HTTP response. The backend returns frames wrapped in a DICOMweb
 *  multipart envelope so that standards-native clients (Slim /
 *  dicom-microscopy-viewer / OHIF) can consume the same endpoint. For
 *  our custom viewport we just want the raw pixel bytes, so we strip
 *  the boundary header, an RFC-822-style part header, and the
 *  trailing `\r\n--boundary--\r\n` marker. */
function stripMultipart(contentType: string | null, raw: Uint8Array): Uint8Array {
  if (!contentType || !contentType.includes("multipart/related")) return raw;
  // Content-Type: multipart/related; ...; boundary=NAME[; ...]
  const m = /boundary=("([^"]+)"|([^;\s]+))/i.exec(contentType);
  if (!m) return raw;
  const boundary = m[2] ?? m[3];
  const enc = new TextEncoder();
  const needle = enc.encode(`--${boundary}`);
  // Find first occurrence of `--boundary`; skip past the CRLF; then
  // skip the part's own header block (terminated by a blank CRLFCRLF).
  let start = indexOf(raw, needle, 0);
  if (start < 0) return raw;
  start += needle.length;
  // Skip CRLF(s) after boundary line.
  while (start < raw.length && (raw[start] === 0x0d || raw[start] === 0x0a)) start++;
  // Locate the blank line CRLFCRLF that ends the part header.
  const headerEnd = findHeaderEnd(raw, start);
  if (headerEnd < 0) return raw;
  // End of payload is right before the next `\r\n--boundary`.
  const tail = indexOf(raw, enc.encode(`\r\n--${boundary}`), headerEnd);
  const end = tail < 0 ? raw.length : tail;
  return raw.subarray(headerEnd, end);
}

function indexOf(buf: Uint8Array, needle: Uint8Array, from: number): number {
  outer: for (let i = from; i <= buf.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (buf[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

function findHeaderEnd(buf: Uint8Array, from: number): number {
  // Look for CRLFCRLF (4 bytes: 0D 0A 0D 0A) OR LFLF (0A 0A) and
  // return the index of the byte AFTER the double line break.
  for (let i = from; i + 3 < buf.length; i++) {
    if (buf[i] === 0x0d && buf[i + 1] === 0x0a && buf[i + 2] === 0x0d && buf[i + 3] === 0x0a)
      return i + 4;
    if (buf[i] === 0x0a && buf[i + 1] === 0x0a)
      return i + 2;
  }
  return -1;
}

/** Fetch one tile, decode it, and return an ImageBitmap ready for
 *  canvas drawImage. The response is always a multipart/related
 *  envelope (DICOMweb retrieve-frames) but the part's Content-Type
 *  tells us whether the payload is a pre-encoded JPEG — the fast
 *  path used by ingest-pathology-jpeg-tiles.py — or raw
 *  uncompressed RGB pulled straight from the DICOM PixelData element
 *  (the original slow path, kept for instances that were ingested
 *  before the JPEG cache existed). The ~10× wire saving comes
 *  almost entirely from the JPEG fast path. */
async function fetchTile(
  pyr: Pyramid, level: PyramidLevel, tx: number, ty: number,
): Promise<Tile> {
  const frame = ty * level.tilesPerRow + tx + 1;
  const url = `${BASE}/wado/studies/${pyr.studyUid}/series/${pyr.seriesUid}/instances/${level.instanceUid}/frames/${frame}`;
  const rsp = await fetch(url, { headers: octetHeaders() });
  if (rsp.status === 401) onAuthFailure();
  if (!rsp.ok) throw new Error(`tile ${frame}: ${rsp.status}`);
  const raw = new Uint8Array(await rsp.arrayBuffer());

  const outerCT = rsp.headers.get("content-type") || "";
  const { partCT, bytes } = stripMultipartWithCT(outerCT, raw);

  // JPEG-2000 gets decoded via our Zig WASM decoder — Chrome/Firefox
  // don't support `image/jp2` natively, and we need lossless output
  // for pathology diagnosis anyway.
  if (isJ2KMediaType(partCT)) {
    const { width, height, rgba } = await decodeJ2K(bytes);
    const bitmap = await createImageBitmap(new ImageData(rgba, width, height));
    return { level, tx, ty, image: bitmap };
  }

  // Plain JPEG (or any other browser-understood image): native decode
  // via Blob → createImageBitmap. No RGB→RGBA copy, no hand-rolled
  // decoder.
  if (partCT.startsWith("image/")) {
    const blob = new Blob([bytes], { type: mediaTypeOf(partCT) });
    const bitmap = await createImageBitmap(blob);
    return { level, tx, ty, image: bitmap };
  }

  // Legacy path: raw RGB / MONOCHROME pixel data straight from DICOM.
  const { rows, cols, samples, bitsAllocated } = level.meta;
  const pixelW = cols, pixelH = rows;
  if (bitsAllocated !== 8) throw new Error(`unsupported bitsAllocated ${bitsAllocated}`);
  const rgba = new Uint8ClampedArray(pixelW * pixelH * 4);
  if (samples === 3) {
    for (let i = 0, j = 0; i < pixelW * pixelH; i++, j += 4) {
      rgba[j]     = bytes[i * 3];
      rgba[j + 1] = bytes[i * 3 + 1];
      rgba[j + 2] = bytes[i * 3 + 2];
      rgba[j + 3] = 0xff;
    }
  } else if (samples === 1) {
    for (let i = 0, j = 0; i < pixelW * pixelH; i++, j += 4) {
      rgba[j] = rgba[j + 1] = rgba[j + 2] = bytes[i];
      rgba[j + 3] = 0xff;
    }
  } else {
    throw new Error(`unsupported samplesPerPixel ${samples}`);
  }

  const imgData = new ImageData(rgba, pixelW, pixelH);
  const bitmap = await createImageBitmap(imgData);
  return { level, tx, ty, image: bitmap };
}

/** Variant of `stripMultipart` that also returns the part's own
 *  Content-Type header. The outer multipart/related envelope always
 *  has `type="..."` but the per-part type is what tells us whether
 *  the body is a JPEG or raw RGB. */
function stripMultipartWithCT(
  outerCT: string,
  raw: Uint8Array,
): { partCT: string; bytes: Uint8Array } {
  if (!outerCT.includes("multipart/related")) {
    return { partCT: outerCT, bytes: raw };
  }
  const m = /boundary=("([^"]+)"|([^;\s]+))/i.exec(outerCT);
  if (!m) return { partCT: outerCT, bytes: raw };
  const boundary = m[2] ?? m[3];
  const enc = new TextEncoder();
  const needle = enc.encode(`--${boundary}`);
  let start = indexOf(raw, needle, 0);
  if (start < 0) return { partCT: "", bytes: raw };
  start += needle.length;
  while (start < raw.length && (raw[start] === 0x0d || raw[start] === 0x0a)) start++;
  const headerEnd = findHeaderEnd(raw, start);
  if (headerEnd < 0) return { partCT: "", bytes: raw };
  // Pull Content-Type out of the part headers.
  const headerText = new TextDecoder("ascii").decode(raw.subarray(start, headerEnd));
  const ctMatch = /^content-type:\s*([^\r\n]+)/im.exec(headerText);
  const partCT = ctMatch ? ctMatch[1].trim() : "";
  const tail = indexOf(raw, enc.encode(`\r\n--${boundary}`), headerEnd);
  const end = tail < 0 ? raw.length : tail;
  return { partCT, bytes: raw.subarray(headerEnd, end) };
}

function mediaTypeOf(ct: string): string {
  // Drop any DICOMweb `transfer-syntax=...` parameter before handing
  // to Blob; `image/jpeg; transfer-syntax=1.2.840…` is valid MIME but
  // some Blob constructors normalise the parameter list and some
  // don't. Cleaner to pass bare `image/jpeg`.
  const semi = ct.indexOf(";");
  return (semi < 0 ? ct : ct.slice(0, semi)).trim();
}

/** Batched tile fetch. Given a set of (tx,ty) coordinates for a single
 *  pyramid level, issues ONE WADO-RS retrieve-frames request with all
 *  the missing frame numbers comma-separated and parses the
 *  multipart/related response. Already-cached tiles are returned from
 *  the cache; already-inflight tiles share their existing Promise.
 *
 *  This is the critical fix for the 80-tile-burst-wedges-the-backend
 *  problem: without batching, each visible tile was a separate fetch
 *  → a separate ingress-nginx stream → a separate Bun proxy JWT
 *  verify → a separate round-trip to vpd-dicomweb, whose
 *  single-threaded accept() loop couldn't keep up and eventually
 *  stopped accepting TCP connections entirely, causing the 502 storm
 *  the user saw in DevTools.
 *
 *  After batching, one zoom-level switch issues exactly one HTTP
 *  request regardless of how many tiles are visible. */
export function getTilesBatch(
  pyr: Pyramid, level: PyramidLevel,
  coords: Array<{ tx: number; ty: number }>,
  onTile?: (tile: Tile) => void,
  opts?: { sticky?: boolean },
): Promise<Tile[]> {
  if (coords.length === 0) return Promise.resolve([]);
  const sticky = opts?.sticky === true;

  // Partition coords: already-cached tiles go straight to the output;
  // already-inflight tiles re-use their Promise; the rest are batched
  // into one network request below.
  const results: Promise<Tile>[] = new Array(coords.length);
  type Need = { idx: number; tx: number; ty: number; frame: number; key: string };
  const need: Need[] = [];

  for (let i = 0; i < coords.length; i++) {
    const { tx, ty } = coords[i];
    const frame = ty * level.tilesPerRow + tx + 1;
    const key   = keyOf({ instanceUid: level.instanceUid, frame });
    const cached = tiles.get(key);
    if (cached) { results[i] = Promise.resolve(cached); continue; }
    const infl = inflight.get(key);
    if (infl)  { results[i] = infl; continue; }
    need.push({ idx: i, tx, ty, frame, key });
  }

  if (need.length > 0) {
    // Sort by frame number so the URL path stays stable across calls
    // — helpful for any caching layer keyed on full URL.
    need.sort((a, b) => a.frame - b.frame);

    const frameCsv = need.map((n) => n.frame).join(",");
    const url = `${BASE}/wado/studies/${pyr.studyUid}/series/${pyr.seriesUid}/instances/${level.instanceUid}/frames/${frameCsv}`;

    // Each tile's Promise resolves independently. We don't use a
    // single batchPromise + Promise.all anymore because one decoder
    // reject (a malformed tile, a WASM OOM on an edge tile, …) would
    // then reject ALL 16 per-tile promises, clearing their inflight
    // entries, so the very next rAF redraw would refetch the whole
    // batch. In production that produced a visible-on-DevTools 2000+
    // request storm on the native-JP2 Ki67 slide.
    //
    // Approach: pre-allocate deferreds, fire the single HTTP request,
    // parse it, then resolve each deferred based on its own tile's
    // success or failure. A tile that fails to decode is marked with
    // a "failed" sentinel in the cache so subsequent draws treat it
    // as handled and DO NOT retry it.
    type Deferred = {
      resolve: (t: Tile) => void;
      reject:  (e: Error) => void;
      promise: Promise<Tile>;
    };
    function makeDeferred(): Deferred {
      let resolve!: (t: Tile) => void;
      let reject!:  (e: Error) => void;
      const promise = new Promise<Tile>((res, rej) => { resolve = res; reject = rej; });
      return { resolve, reject, promise };
    }
    const deferreds: Deferred[] = need.map(() => makeDeferred());

    (async () => {
      let parts: Array<{ partCT: string; bytes: Uint8Array }> = [];
      let outerError: Error | null = null;
      try {
        const rsp = await fetch(url, { headers: octetHeaders() });
        if (rsp.status === 401) onAuthFailure();
        if (!rsp.ok) throw new Error(`batch ${frameCsv.slice(0, 40)}…: ${rsp.status}`);
        const raw = new Uint8Array(await rsp.arrayBuffer());
        const outerCT = rsp.headers.get("content-type") ?? "";
        parts = splitMultipartAll(outerCT, raw);
        if (parts.length !== need.length) {
          throw new Error(`expected ${need.length} parts, got ${parts.length}`);
        }
      } catch (err) {
        outerError = err instanceof Error ? err : new Error(String(err));
      }
      if (outerError) {
        // Network-level failure: reject every tile in this batch.
        // These stay un-cached so a future redraw CAN retry if the
        // user wanders back into that region. Inflight-clear happens
        // via each Promise's .finally below.
        for (const d of deferreds) d.reject(outerError);
        return;
      }
      // Per-tile decode, independent of siblings. Promise.all deliberately
      // NOT used — one failure must not cascade.
      await Promise.all(parts.map(async (part, i) => {
        const n = need[i];
        try {
          let bitmap: ImageBitmap;
          if (isJ2KMediaType(part.partCT)) {
            const { width, height, rgba } = await decodeJ2K(part.bytes);
            bitmap = await createImageBitmap(new ImageData(rgba, width, height));
          } else if (part.partCT.startsWith("image/")) {
            bitmap = await createImageBitmap(new Blob([part.bytes], { type: mediaTypeOf(part.partCT) }));
          } else {
            bitmap = await decodeRawRgb(level, part.bytes);
          }
          const tile: Tile = { level, tx: n.tx, ty: n.ty, image: bitmap };
          storeTile(n.key, tile, sticky);
          // Clear any prior failure latch — a successful decode means
          // the tile is good now, and a future LRU-eviction-then-fail
          // sequence should get a fresh retry budget.
          failedTiles.delete(n.key);
          if (onTile) try { onTile(tile); } catch { /* caller owns errors */ }
          deferreds[i].resolve(tile);
        } catch (err) {
          // Decode failure: latch in `failedTiles` so the rAF redraw
          // doesn't re-enqueue this exact tile and chase its own tail.
          // Schedule a background retry with backoff (up to
          // MAX_TILE_RETRIES) so transient corruption (e.g. partial
          // multipart body, J2K TruncatedStream) recovers without the
          // user having to click the black square.
          const attempts = (failedTiles.get(n.key) ?? 0) + 1;
          failedTiles.set(n.key, attempts);
          // eslint-disable-next-line no-console
          console.warn(`[wsi] tile decode failed at (${n.tx},${n.ty}) frame=${n.frame} attempt=${attempts}/${MAX_TILE_RETRIES}: ${err instanceof Error ? err.message : String(err)}`);
          deferreds[i].reject(err instanceof Error ? err : new Error(String(err)));
          if (attempts < MAX_TILE_RETRIES) {
            const delay = RETRY_BACKOFF_MS[Math.min(attempts - 1, RETRY_BACKOFF_MS.length - 1)];
            setTimeout(() => {
              // Re-enqueue this single tile. We deliberately leave the
              // `failedTiles` entry in place so the rAF redraw still
              // skips it (only this setTimeout drives the retry); the
              // entry's count is incremented by the new attempt's catch
              // handler if it fails, or it's overwritten by the success
              // path which never reads it. onTile fires when the retry
              // decode succeeds, which the viewport's redraw callback
              // uses to repaint the square.
              getTilesBatch(pyr, level, [{ tx: n.tx, ty: n.ty }], onTile, opts).catch(() => {});
            }, delay);
          }
        }
      }));
    })();

    for (let j = 0; j < need.length; j++) {
      const n = need[j];
      const p = deferreds[j].promise.finally(() => inflight.delete(n.key));
      inflight.set(n.key, p);
      results[n.idx] = p;
    }
  }

  return Promise.all(results);
}

/** Raw-RGB decoder path used for instances that were ingested before
 *  the JPEG-tile pipeline existed. Matches the byte layout the backend
 *  serves for them: samples * rows * cols bytes straight from DICOM
 *  PixelData. */
async function decodeRawRgb(level: PyramidLevel, bytes: Uint8Array): Promise<ImageBitmap> {
  const { rows, cols, samples, bitsAllocated } = level.meta;
  if (bitsAllocated !== 8) throw new Error(`unsupported bitsAllocated ${bitsAllocated}`);
  const rgba = new Uint8ClampedArray(cols * rows * 4);
  if (samples === 3) {
    for (let i = 0, j = 0; i < cols * rows; i++, j += 4) {
      rgba[j]     = bytes[i * 3];
      rgba[j + 1] = bytes[i * 3 + 1];
      rgba[j + 2] = bytes[i * 3 + 2];
      rgba[j + 3] = 0xff;
    }
  } else if (samples === 1) {
    for (let i = 0, j = 0; i < cols * rows; i++, j += 4) {
      rgba[j] = rgba[j + 1] = rgba[j + 2] = bytes[i];
      rgba[j + 3] = 0xff;
    }
  } else {
    throw new Error(`unsupported samplesPerPixel ${samples}`);
  }
  return createImageBitmap(new ImageData(rgba, cols, rows));
}

/** Walk every part of a multipart/related response and yield
 *  { partCT, bytes } tuples in order. Companion to
 *  `stripMultipartWithCT` but returns every part, not just the
 *  first. Used by the batched tile path. */
function splitMultipartAll(outerCT: string, raw: Uint8Array): Array<{ partCT: string; bytes: Uint8Array }> {
  if (!outerCT.includes("multipart/related")) {
    return [{ partCT: outerCT, bytes: raw }];
  }
  const m = /boundary=("([^"]+)"|([^;\s]+))/i.exec(outerCT);
  if (!m) return [{ partCT: outerCT, bytes: raw }];
  const boundary = m[2] ?? m[3];
  const enc = new TextEncoder();
  const delim = enc.encode(`\r\n--${boundary}`);
  const firstDelim = enc.encode(`--${boundary}`);

  const out: Array<{ partCT: string; bytes: Uint8Array }> = [];
  // Walk from the first boundary onward; each chunk between
  // boundaries is a body-part (except the closing `--boundary--`).
  let cursor = indexOf(raw, firstDelim, 0);
  if (cursor < 0) return [{ partCT: "", bytes: raw }];
  cursor += firstDelim.length;
  while (cursor < raw.length) {
    while (cursor < raw.length && (raw[cursor] === 0x0d || raw[cursor] === 0x0a)) cursor++;
    // Closing boundary `--`?
    if (cursor + 1 < raw.length && raw[cursor] === 0x2d && raw[cursor + 1] === 0x2d) break;
    const hEnd = findHeaderEnd(raw, cursor);
    if (hEnd < 0) break;
    const hdr = new TextDecoder("ascii").decode(raw.subarray(cursor, hEnd));
    const ctMatch = /^content-type:\s*([^\r\n]+)/im.exec(hdr);
    const partCT = ctMatch ? ctMatch[1].trim() : "";
    const nextDelim = indexOf(raw, delim, hEnd);
    const bodyEnd = nextDelim < 0 ? raw.length : nextDelim;
    out.push({ partCT, bytes: raw.subarray(hEnd, bodyEnd) });
    if (nextDelim < 0) break;
    cursor = nextDelim + delim.length;
  }
  return out;
}

/** Single-tile helper kept for legacy call-sites (prefetch ring,
 *  minimap). Internally just calls the batch function. Callers can
 *  set `sticky: true` for tiles that must never be LRU-evicted (the
 *  minimap's coarsest-level set; those thrash otherwise). */
export function getTile(
  pyr: Pyramid, level: PyramidLevel, tx: number, ty: number,
  opts?: { sticky?: boolean },
): Promise<Tile> {
  const frame = ty * level.tilesPerRow + tx + 1;
  const k = keyOf({ instanceUid: level.instanceUid, frame });
  const cached = stickyTiles.get(k) ?? tiles.get(k);
  if (cached) return Promise.resolve(cached);
  if (failedTiles.has(k)) {
    return Promise.reject(new Error(`tile (${tx},${ty}) previously failed`));
  }
  const inFlight = inflight.get(k);
  if (inFlight) return inFlight;
  const sticky = opts?.sticky === true;
  const p = fetchTile(pyr, level, tx, ty).then((t) => {
    storeTile(k, t, sticky);
    return t;
  }, (err) => {
    // Any decode / network failure is latched so the next redraw
    // doesn't immediately re-queue this exact tile. Without this the
    // minimap loop on a large slide produces the 2000-rps storm the
    // pathologist-demo session surfaced.
    const attempts = (failedTiles.get(k) ?? 0) + 1;
    failedTiles.set(k, attempts);
    // eslint-disable-next-line no-console
    console.warn(`[wsi] single-tile fetch failed at (${tx},${ty}) frame=${frame} attempt=${attempts}/${MAX_TILE_RETRIES}: ${err instanceof Error ? err.message : String(err)}`);
    throw err;
  }).finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}

/** Prefetch the ring of neighbour tiles around the visible set so
 *  small pan motions don't show blank gaps. Best-effort — failures
 *  are swallowed. */
export function prefetchRing(
  pyr: Pyramid, level: PyramidLevel,
  visible: Array<{ tx: number; ty: number }>,
  ring = 1,
): void {
  const seen = new Set<string>();
  for (const v of visible) seen.add(`${v.tx},${v.ty}`);
  for (const v of visible) {
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (dx === 0 && dy === 0) continue;
        const tx = v.tx + dx, ty = v.ty + dy;
        if (tx < 0 || ty < 0 || tx >= level.tilesPerRow || ty >= level.tilesPerCol) continue;
        const k = `${tx},${ty}`;
        if (seen.has(k)) continue;
        seen.add(k);
        getTile(pyr, level, tx, ty).catch(() => { /* best-effort */ });
      }
    }
  }
}

export function clearTileCache() {
  for (const t of tiles.values()) t.image.close();
  tiles.clear();
  inflight.clear();
}
